import React, { useEffect, useMemo, useRef, useState } from 'react';
import jsPDF from 'jspdf';
import {
  FaDownload,
  FaPrint,
  FaRedo,
  FaGasPump,
  FaRupeeSign,
  FaUpload,
  FaTrash,
} from 'react-icons/fa';
import SEO from '../components/SEO';
import {
  FUEL_BRANDS,
  getBrand,
  loadLogoAsPng,
  hexToRgb,
} from '../utils/fuelBrands';

const FUEL_TYPES = ['petrol', 'diesel', 'CNG', 'premium petrol', 'premium diesel'];
const PAYMENT_MODES = ['cash', 'card', 'UPI', 'wallet', 'fleet card'];
const INPUT_MODES = [
  { value: 'amount', label: 'Enter Amount (₹)' },
  { value: 'quantity', label: 'Enter Volume (L)' },
];

const TEMPLATES = [
  { id: 'classic', label: 'Classic Thermal', format: [80, 200] },
  { id: 'banner', label: 'Bold Brand Banner', format: [80, 200] },
  { id: 'minimal', label: 'Minimal Mono', format: [80, 200] },
  { id: 'sidebar', label: 'Side Strip', format: [80, 200] },
  { id: 'invoice', label: 'A4 GST Invoice', format: 'a4' },
];

const pad2 = (n) => String(n).padStart(2, '0');
const todayDMY = () => {
  const d = new Date();
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
};
const nowHM = () => {
  const d = new Date();
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};
const generateInvoiceNumber = () => {
  const year = new Date().getFullYear();
  const seq = Math.floor(1000 + Math.random() * 9000);
  return `INV-${year}-${seq}`;
};

const CUSTOM_LOGO_STORAGE_KEY = 'petrolBill.customLogos.v1';

const readCustomLogos = () => {
  try {
    const raw = localStorage.getItem(CUSTOM_LOGO_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (e) {
    return {};
  }
};

const writeCustomLogos = (data) => {
  try {
    localStorage.setItem(CUSTOM_LOGO_STORAGE_KEY, JSON.stringify(data));
  } catch (e) {
    // Storage may be full (data URIs can be large) — fail silently.
  }
};

const readFileAsDataURL = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const PetrolBillGenerator = () => {
  const [brandId, setBrandId] = useState('indianoil');
  const brand = getBrand(brandId);

  const [template, setTemplate] = useState('classic');

  const [customLogos, setCustomLogos] = useState(() => readCustomLogos());
  const fileInputRef = useRef(null);

  const activeLogoUrl = customLogos[brandId] || brand.logoUrl;

  const handleLogoUpload = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('Please choose an image file (PNG, JPG, SVG, WebP).');
      return;
    }
    if (file.size > 1.5 * 1024 * 1024) {
      alert('Logo file is too large. Please pick an image under 1.5 MB.');
      return;
    }
    try {
      const dataUrl = await readFileAsDataURL(file);
      const next = { ...customLogos, [brandId]: dataUrl };
      setCustomLogos(next);
      writeCustomLogos(next);
    } catch (e) {
      alert('Could not read that image. Please try another file.');
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleClearCustomLogo = () => {
    if (!customLogos[brandId]) return;
    const next = { ...customLogos };
    delete next[brandId];
    setCustomLogos(next);
    writeCustomLogos(next);
  };

  const [customer, setCustomer] = useState({
    name: 'Anaya Verma',
    address: '12 Lakeview Road, Hyderabad, Delhi 853334',
  });

  const [copyLabel, setCopyLabel] = useState('ORIGINAL');

  const [meta, setMeta] = useState({
    date: todayDMY(),
    time: nowHM(),
    gstin: '12YANOJ1665C9Z5',
    invoiceNo: generateInvoiceNumber(),
    vehicleNo: 'KL 43',
    nozzleNo: '2',
    density: '',
    paymentMode: 'card',
  });

  const [fuelType, setFuelType] = useState('petrol');
  const [ratePerLitre, setRatePerLitre] = useState(102.78);
  const [inputMode, setInputMode] = useState('amount');
  const [amountInput, setAmountInput] = useState(6900);
  const [volumeInput, setVolumeInput] = useState(67.13);

  const computed = useMemo(() => {
    const rate = Number(ratePerLitre) || 0;
    let volume = 0;
    let amount = 0;
    if (inputMode === 'amount') {
      amount = Number(amountInput) || 0;
      volume = rate > 0 ? amount / rate : 0;
    } else {
      volume = Number(volumeInput) || 0;
      amount = volume * rate;
    }
    return {
      rate,
      volume: Number(volume.toFixed(2)),
      amount: Math.round(amount * 100) / 100,
    };
  }, [inputMode, amountInput, volumeInput, ratePerLitre]);

  useEffect(() => {
    if (inputMode === 'amount') {
      setVolumeInput(computed.volume);
    } else {
      setAmountInput(computed.amount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [computed.volume, computed.amount, inputMode]);

  const handleReset = () => {
    setBrandId('indianoil');
    setCustomer({ name: '', address: '' });
    setCopyLabel('ORIGINAL');
    setMeta({
      date: todayDMY(),
      time: nowHM(),
      gstin: '',
      invoiceNo: generateInvoiceNumber(),
      vehicleNo: '',
      nozzleNo: '',
      density: '',
      paymentMode: 'cash',
    });
    setFuelType('petrol');
    setRatePerLitre(0);
    setInputMode('amount');
    setAmountInput(0);
    setVolumeInput(0);
  };

  // ---------- SHARED PDF HELPERS ----------
  const getLogoPng = async () => {
    try {
      return await loadLogoAsPng(activeLogoUrl, 360);
    } catch (e) {
      return null;
    }
  };

  const drawLogoOrCircle = (doc, png, x, y, size) => {
    if (png) {
      doc.addImage(png, 'PNG', x, y, size, size);
    } else {
      const [pr, pg, pb] = hexToRgb(brand.primary);
      doc.setFillColor(pr, pg, pb);
      doc.circle(x + size / 2, y + size / 2, size / 2, 'F');
    }
  };

  // ---------- TEMPLATE 1: CLASSIC THERMAL ----------
  const renderClassic = async () => {
    const w = 80;
    const h = 200;
    const doc = new jsPDF({ unit: 'mm', format: [w, h] });
    const left = 5;
    const right = w - 5;
    let y = 10;

    const png = await getLogoPng();
    const logoSize = 26;
    drawLogoOrCircle(doc, png, (w - logoSize) / 2, y, logoSize);
    y += logoSize + 6;

    if (customer.name) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      doc.text(customer.name, w / 2, y, { align: 'center' });
      y += 5;
    }
    if (customer.address) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      const lines = doc.splitTextToSize(customer.address, w - 10);
      lines.forEach((ln) => {
        doc.text(ln, w / 2, y, { align: 'center' });
        y += 4;
      });
    }
    if (copyLabel) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.text(copyLabel.toUpperCase(), w / 2, y, { align: 'center' });
      y += 6;
    }

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(meta.date || '-', left, y);
    doc.text(meta.time || '-', right, y, { align: 'right' });
    y += 7;

    const row = (label, value) => {
      if (value === undefined || value === null || value === '') return;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.text(`${label}:`, left, y);
      doc.setFont('helvetica', 'normal');
      doc.text(String(value), right, y, { align: 'right' });
      y += 6;
    };

    row('GSTIN', meta.gstin);
    row('INVOICE NO', meta.invoiceNo);
    row('VEHICLE NO', meta.vehicleNo);
    row('NOZZLE NO', meta.nozzleNo);
    row('PRODUCT', fuelType);
    row('DENSITY', meta.density ? `${meta.density} Kg/m3` : '-  Kg/m3');
    row('RATE', `${computed.rate.toFixed(2)}  INR/Ltr`);
    row('VOLUME', `${computed.volume.toFixed(2)}  Ltr`);
    row('AMOUNT', `${Math.round(computed.amount)}  INR`);
    row('MODE', meta.paymentMode);

    y += 6;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    const [pr, pg, pb] = hexToRgb(brand.primary);
    doc.setTextColor(pr, pg, pb);
    doc.text('Thank you Visit Again', w / 2, y, { align: 'center' });
    return doc;
  };

  // ---------- TEMPLATE 2: BOLD BRAND BANNER ----------
  const renderBanner = async () => {
    const w = 80;
    const h = 200;
    const doc = new jsPDF({ unit: 'mm', format: [w, h] });
    const left = 5;
    const right = w - 5;
    const [pr, pg, pb] = hexToRgb(brand.primary);
    let y = 0;

    // Colored banner with logo + brand name
    doc.setFillColor(pr, pg, pb);
    doc.rect(0, 0, w, 30, 'F');
    const png = await getLogoPng();
    drawLogoOrCircle(doc, png, 4, 4, 22);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(255, 255, 255);
    doc.text(brand.name.toUpperCase(), 30, 14);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text('TAX INVOICE', 30, 20);
    if (meta.gstin) doc.text(`GSTIN ${meta.gstin}`, 30, 25);
    y = 36;

    doc.setTextColor(0, 0, 0);
    doc.setFontSize(9);
    if (customer.name) {
      doc.setFont('helvetica', 'bold');
      doc.text(customer.name, left, y);
      y += 4;
    }
    if (customer.address) {
      doc.setFont('helvetica', 'normal');
      const lines = doc.splitTextToSize(customer.address, w - 10);
      lines.forEach((ln) => { doc.text(ln, left, y); y += 4; });
    }
    if (copyLabel) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setFillColor(pr, pg, pb);
      doc.setTextColor(255, 255, 255);
      doc.rect(right - 32, y - 4, 32, 6, 'F');
      doc.text(copyLabel.toUpperCase(), right - 16, y, { align: 'center' });
      doc.setTextColor(0, 0, 0);
      y += 4;
    }
    y += 4;
    doc.setDrawColor(pr, pg, pb);
    doc.setLineWidth(0.3);
    doc.line(left, y, right, y);
    y += 4;

    doc.setFontSize(9);
    doc.text(`Date: ${meta.date || '-'}`, left, y);
    doc.text(`Time: ${meta.time || '-'}`, right, y, { align: 'right' });
    y += 5;

    const row = (label, value) => {
      if (value === undefined || value === null || value === '') return;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.text(label, left, y);
      doc.text(String(value), right, y, { align: 'right' });
      y += 5;
    };
    row('Invoice', meta.invoiceNo);
    row('Vehicle', meta.vehicleNo);
    row('Nozzle', meta.nozzleNo);
    row('Product', fuelType);
    row('Density', meta.density ? `${meta.density} Kg/m3` : '-');
    row('Rate', `${computed.rate.toFixed(2)} /L`);
    row('Volume', `${computed.volume.toFixed(2)} L`);

    y += 2;
    doc.setFillColor(pr, pg, pb);
    doc.rect(left - 1, y, right - left + 2, 11, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(255, 255, 255);
    doc.text('TOTAL', left + 1, y + 7);
    doc.text(`Rs. ${Math.round(computed.amount)}`, right - 1, y + 7, { align: 'right' });
    y += 15;

    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(`Paid via ${meta.paymentMode}`, w / 2, y, { align: 'center' });
    y += 8;
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(pr, pg, pb);
    doc.text('Thank you Visit Again', w / 2, y, { align: 'center' });
    return doc;
  };

  // ---------- TEMPLATE 3: MINIMAL MONO ----------
  const renderMinimal = async () => {
    const w = 80;
    const h = 200;
    const doc = new jsPDF({ unit: 'mm', format: [w, h] });
    const left = 5;
    const right = w - 5;
    let y = 12;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text('FUEL RECEIPT', w / 2, y, { align: 'center' });
    y += 4;
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.4);
    doc.line(left, y, right, y);
    y += 8;

    if (customer.name) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.text(customer.name, w / 2, y, { align: 'center' });
      y += 5;
    }
    if (customer.address) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      const lines = doc.splitTextToSize(customer.address, w - 10);
      lines.forEach((ln) => { doc.text(ln, w / 2, y, { align: 'center' }); y += 3.5; });
    }
    y += 4;

    // Big mono amount
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(22);
    doc.text(`Rs. ${Math.round(computed.amount)}`, w / 2, y, { align: 'center' });
    y += 4;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(`${computed.volume.toFixed(2)} L  @  Rs. ${computed.rate.toFixed(2)}/L`, w / 2, y, { align: 'center' });
    y += 6;
    doc.setDrawColor(180, 180, 180);
    doc.setLineWidth(0.2);
    doc.line(left, y, right, y);
    y += 6;

    const row = (label, value) => {
      if (value === undefined || value === null || value === '') return;
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 116, 139);
      doc.text(label, left, y);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text(String(value), right, y, { align: 'right' });
      y += 5;
    };
    row('Date', `${meta.date} ${meta.time}`);
    row('Invoice', meta.invoiceNo);
    row('Vehicle', meta.vehicleNo);
    row('Nozzle', meta.nozzleNo);
    row('Product', fuelType);
    row('Payment', meta.paymentMode);
    if (copyLabel) row('Copy', copyLabel);

    y += 8;
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.text('Thank you. Drive safe.', w / 2, y, { align: 'center' });
    return doc;
  };

  // ---------- TEMPLATE 4: SIDE STRIP ----------
  const renderSidebar = async () => {
    const w = 80;
    const h = 200;
    const doc = new jsPDF({ unit: 'mm', format: [w, h] });
    const [pr, pg, pb] = hexToRgb(brand.primary);
    const stripW = 8;
    const left = stripW + 4;
    const right = w - 4;

    // Vertical color strip
    doc.setFillColor(pr, pg, pb);
    doc.rect(0, 0, stripW, h, 'F');

    let y = 10;
    const png = await getLogoPng();
    const logoSize = 22;
    drawLogoOrCircle(doc, png, (w + stripW - logoSize) / 2, y, logoSize);
    y += logoSize + 4;

    if (customer.name) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(0, 0, 0);
      doc.text(customer.name, (w + stripW) / 2, y, { align: 'center' });
      y += 5;
    }
    if (customer.address) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      const lines = doc.splitTextToSize(customer.address, w - stripW - 8);
      lines.forEach((ln) => { doc.text(ln, (w + stripW) / 2, y, { align: 'center' }); y += 3.5; });
    }
    y += 3;
    if (copyLabel) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(pr, pg, pb);
      doc.text(copyLabel.toUpperCase(), (w + stripW) / 2, y, { align: 'center' });
      doc.setTextColor(0, 0, 0);
      y += 5;
    }

    doc.setDrawColor(pr, pg, pb);
    doc.setLineWidth(0.3);
    doc.line(left, y, right, y);
    y += 5;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(meta.date || '-', left, y);
    doc.text(meta.time || '-', right, y, { align: 'right' });
    y += 6;

    const row = (label, value) => {
      if (value === undefined || value === null || value === '') return;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.text(`${label}:`, left, y);
      doc.setFont('helvetica', 'normal');
      doc.text(String(value), right, y, { align: 'right' });
      y += 5;
    };
    row('GSTIN', meta.gstin);
    row('INVOICE', meta.invoiceNo);
    row('VEHICLE', meta.vehicleNo);
    row('NOZZLE', meta.nozzleNo);
    row('PRODUCT', fuelType);
    row('DENSITY', meta.density ? `${meta.density} Kg/m3` : '-');
    row('RATE', `${computed.rate.toFixed(2)} /L`);
    row('VOLUME', `${computed.volume.toFixed(2)} L`);
    row('AMOUNT', `Rs. ${Math.round(computed.amount)}`);
    row('MODE', meta.paymentMode);

    y += 6;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(pr, pg, pb);
    doc.text('Thank you Visit Again', (w + stripW) / 2, y, { align: 'center' });
    return doc;
  };

  // ---------- TEMPLATE 5: A4 GST INVOICE ----------
  const renderInvoice = async () => {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const w = 210;
    const left = 14;
    const right = w - 14;
    const [pr, pg, pb] = hexToRgb(brand.primary);
    let y = 14;

    // Header band
    doc.setFillColor(pr, pg, pb);
    doc.rect(0, 0, w, 30, 'F');
    const png = await getLogoPng();
    drawLogoOrCircle(doc, png, left, 4, 22);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    doc.setTextColor(255, 255, 255);
    doc.text(brand.name.toUpperCase(), left + 28, 14);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text('FUEL TAX INVOICE', left + 28, 22);
    if (copyLabel) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.text(copyLabel.toUpperCase(), right, 14, { align: 'right' });
    }
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    if (meta.gstin) doc.text(`GSTIN: ${meta.gstin}`, right, 22, { align: 'right' });

    y = 40;
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('Billed To', left, y);
    doc.text('Invoice Details', w / 2, y);
    y += 6;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(customer.name || '-', left, y);
    doc.text(`Invoice No: ${meta.invoiceNo || '-'}`, w / 2, y);
    y += 5;
    const addrLines = doc.splitTextToSize(customer.address || '-', (w / 2) - left - 4);
    doc.text(addrLines, left, y);
    doc.text(`Date: ${meta.date}   Time: ${meta.time}`, w / 2, y);
    y += Math.max(addrLines.length * 5, 5) + 2;
    doc.text(`Vehicle: ${meta.vehicleNo || '-'}`, w / 2, y);
    y += 8;

    // Table header
    doc.setFillColor(pr, pg, pb);
    doc.rect(left, y, right - left, 8, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('Product', left + 2, y + 5);
    doc.text('Nozzle', left + 60, y + 5);
    doc.text('Density', left + 85, y + 5);
    doc.text('Rate (Rs./L)', right - 60, y + 5, { align: 'right' });
    doc.text('Volume (L)', right - 30, y + 5, { align: 'right' });
    doc.text('Amount (Rs.)', right - 2, y + 5, { align: 'right' });
    y += 8;

    // Row
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.rect(left, y, right - left, 10);
    doc.text(fuelType, left + 2, y + 6);
    doc.text(String(meta.nozzleNo || '-'), left + 60, y + 6);
    doc.text(meta.density ? `${meta.density}` : '-', left + 85, y + 6);
    doc.text(computed.rate.toFixed(2), right - 60, y + 6, { align: 'right' });
    doc.text(computed.volume.toFixed(2), right - 30, y + 6, { align: 'right' });
    doc.text(String(Math.round(computed.amount)), right - 2, y + 6, { align: 'right' });
    y += 14;

    // Totals
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('TOTAL', right - 60, y, { align: 'right' });
    doc.setTextColor(pr, pg, pb);
    doc.text(`Rs. ${Math.round(computed.amount)}`, right - 2, y, { align: 'right' });
    doc.setTextColor(0, 0, 0);
    y += 8;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`Payment Mode: ${meta.paymentMode}`, left, y);
    y += 20;

    // Signature
    doc.setDrawColor(120, 120, 120);
    doc.line(right - 60, y, right, y);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('Authorised Signatory', right - 30, y + 6, { align: 'center' });

    // Footer
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(9);
    doc.setTextColor(pr, pg, pb);
    doc.text('Thank you. Visit Again.', w / 2, 285, { align: 'center' });
    return doc;
  };

  const TEMPLATE_RENDERERS = {
    classic: renderClassic,
    banner: renderBanner,
    minimal: renderMinimal,
    sidebar: renderSidebar,
    invoice: renderInvoice,
  };

  const buildPDF = async () => {
    const renderer = TEMPLATE_RENDERERS[template] || renderClassic;
    return await renderer();
  };

  const handleDownloadPDF = async () => {
    const doc = await buildPDF();
    doc.save(`Fuel_Bill_${meta.invoiceNo || 'receipt'}.pdf`);
  };

  const handlePrint = async () => {
    const doc = await buildPDF();
    doc.autoPrint();
    const url = doc.output('bloburl');
    window.open(url, '_blank');
  };

  return (
    <div className="calculator-page fade-in">
      <SEO
        title="Fuel Bill Generator"
        description="Free Fuel Bill / Petrol Receipt Generator with IndianOil, BPCL, HPCL, Reliance, Nayara and Shell branding. Create printable petrol, diesel and CNG bills and download as PDF."
        keywords="fuel bill generator, petrol bill generator, diesel bill, CNG bill, indian oil bill, bpcl bill, hpcl bill, reliance fuel bill, shell bill, fuel receipt pdf"
        canonicalPath="/petrol-bill"
        calculatorType="FuelBillGenerator"
        calculatorDescription="Generate printable Indian fuel-pump bills with brand logo, rate, volume and vehicle number. Download as PDF."
      />

      <div className="calculator-header">
        <h1>Fuel Bill Generator</h1>
        <p>Create a printable Indian fuel-pump receipt and download as PDF</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          <h3 style={{ marginBottom: 12 }}>Choose Fuel Brand</h3>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
              gap: 10,
              marginBottom: 16,
            }}
          >
            {FUEL_BRANDS.map((b) => (
              <button
                key={b.id}
                type="button"
                onClick={() => setBrandId(b.id)}
                style={{
                  border:
                    brandId === b.id
                      ? `2px solid ${b.primary}`
                      : '1px solid var(--border-color, #cbd5e1)',
                  background:
                    brandId === b.id
                      ? `${b.primary}10`
                      : 'var(--card-background, #fff)',
                  borderRadius: 10,
                  padding: '10px 8px',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <img
                  src={customLogos[b.id] || b.logoUrl}
                  alt={b.name}
                  style={{ width: 44, height: 44, objectFit: 'contain' }}
                />
                <span style={{ fontSize: 12, fontWeight: 600, color: b.primary }}>
                  {b.name}
                </span>
              </button>
            ))}
          </div>

          <div
            style={{
              display: 'flex',
              gap: 8,
              flexWrap: 'wrap',
              alignItems: 'center',
              marginBottom: 16,
              padding: '10px 12px',
              border: '1px dashed var(--border-color, #cbd5e1)',
              borderRadius: 10,
              background: 'var(--background-color, #f8fafc)',
            }}
          >
            <span style={{ fontSize: 13, fontWeight: 600 }}>
              Logo for <span style={{ color: brand.primary }}>{brand.name}</span>:
            </span>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/svg+xml,image/webp"
              onChange={handleLogoUpload}
              style={{ display: 'none' }}
            />
            <button
              type="button"
              className="btn btn-outline"
              style={{ padding: '6px 12px' }}
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
            >
              <FaUpload style={{ marginRight: 6 }} />
              {customLogos[brandId] ? 'Replace Logo' : 'Upload Real Logo'}
            </button>
            {customLogos[brandId] && (
              <button
                type="button"
                className="btn btn-outline"
                style={{ padding: '6px 12px' }}
                onClick={handleClearCustomLogo}
              >
                <FaTrash style={{ marginRight: 6 }} /> Use Default
              </button>
            )}
            <span style={{ fontSize: 11, color: 'var(--text-tertiary, #64748b)', flexBasis: '100%' }}>
              PNG / JPG / SVG / WebP, up to 1.5 MB. Stored only in your browser.
            </span>
          </div>

          <h3 style={{ marginBottom: 12 }}>Bill Template</h3>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
            {TEMPLATES.map((t) => {
              const active = template === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTemplate(t.id)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: 999,
                    border: `1.5px solid ${active ? brand.primary : '#cbd5e1'}`,
                    background: active ? brand.primary : '#fff',
                    color: active ? '#fff' : '#0f172a',
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  {t.label}
                </button>
              );
            })}
          </div>

          <h3 style={{ marginBottom: 12 }}>Customer</h3>
          <div className="input-group">
            <label>Customer Name</label>
            <input
              type="text"
              value={customer.name}
              onChange={(e) => setCustomer({ ...customer, name: e.target.value })}
              placeholder="e.g. Anaya Verma"
            />
          </div>
          <div className="input-group">
            <label>Address</label>
            <input
              type="text"
              value={customer.address}
              onChange={(e) => setCustomer({ ...customer, address: e.target.value })}
              placeholder="e.g. 12 Lakeview Road, Hyderabad, Delhi 853334"
            />
          </div>
          <div className="input-group">
            <label>Copy Label</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
              {['ORIGINAL', 'DUPLICATE', 'CUSTOMER COPY', ''].map((opt) => (
                <button
                  key={opt || 'none'}
                  type="button"
                  className={`btn ${copyLabel === opt ? 'btn-primary' : 'btn-outline'}`}
                  style={{ padding: '6px 12px' }}
                  onClick={() => setCopyLabel(opt)}
                >
                  {opt || 'None'}
                </button>
              ))}
            </div>
          </div>

          <h3 style={{ marginTop: 16, marginBottom: 12 }}>Receipt Info</h3>
          <div className="flex-row">
            <div className="input-group" style={{ flex: 1 }}>
              <label>Date (DD/MM/YYYY)</label>
              <input
                type="text"
                value={meta.date}
                onChange={(e) => setMeta({ ...meta, date: e.target.value })}
              />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Time (HH:MM)</label>
              <input
                type="text"
                value={meta.time}
                onChange={(e) => setMeta({ ...meta, time: e.target.value })}
              />
            </div>
          </div>

          <div className="flex-row">
            <div className="input-group" style={{ flex: 1 }}>
              <label>GSTIN</label>
              <input
                type="text"
                value={meta.gstin}
                onChange={(e) => setMeta({ ...meta, gstin: e.target.value.toUpperCase() })}
              />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Invoice No</label>
              <input
                type="text"
                value={meta.invoiceNo}
                onChange={(e) => setMeta({ ...meta, invoiceNo: e.target.value })}
              />
            </div>
          </div>

          <div className="flex-row">
            <div className="input-group" style={{ flex: 1 }}>
              <label>Vehicle No</label>
              <input
                type="text"
                value={meta.vehicleNo}
                onChange={(e) =>
                  setMeta({ ...meta, vehicleNo: e.target.value.toUpperCase() })
                }
              />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Nozzle No</label>
              <input
                type="text"
                value={meta.nozzleNo}
                onChange={(e) => setMeta({ ...meta, nozzleNo: e.target.value })}
              />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Density (Kg/m³)</label>
              <input
                type="text"
                value={meta.density}
                onChange={(e) => setMeta({ ...meta, density: e.target.value })}
                placeholder="optional"
              />
            </div>
          </div>

          <h3 style={{ marginTop: 16, marginBottom: 12 }}>Fuel Details</h3>
          <div className="input-group">
            <label>Product</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
              {FUEL_TYPES.map((t) => (
                <button
                  key={t}
                  type="button"
                  className={`btn ${fuelType === t ? 'btn-primary' : 'btn-outline'}`}
                  style={{ padding: '6px 12px' }}
                  onClick={() => setFuelType(t)}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          <div className="input-group">
            <label>Rate per Litre (₹)</label>
            <div className="input-with-icon">
              <FaRupeeSign className="icon" />
              <input
                type="number"
                value={ratePerLitre === '' ? '' : ratePerLitre}
                onChange={(e) =>
                  setRatePerLitre(e.target.value === '' ? '' : Number(e.target.value))
                }
                min="0"
                step="0.01"
              />
            </div>
          </div>

          <div className="input-group">
            <label>Input Mode</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
              {INPUT_MODES.map((m) => (
                <button
                  key={m.value}
                  type="button"
                  className={`btn ${inputMode === m.value ? 'btn-primary' : 'btn-outline'}`}
                  style={{ padding: '6px 12px' }}
                  onClick={() => setInputMode(m.value)}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          {inputMode === 'amount' ? (
            <div className="input-group">
              <label>Amount Paid (₹)</label>
              <div className="input-with-icon">
                <FaRupeeSign className="icon" />
                <input
                  type="number"
                  value={amountInput === '' ? '' : amountInput}
                  onChange={(e) =>
                    setAmountInput(e.target.value === '' ? '' : Number(e.target.value))
                  }
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
          ) : (
            <div className="input-group">
              <label>Volume (Litres)</label>
              <input
                type="number"
                value={volumeInput === '' ? '' : volumeInput}
                onChange={(e) =>
                  setVolumeInput(e.target.value === '' ? '' : Number(e.target.value))
                }
                min="0"
                step="0.01"
              />
            </div>
          )}

          <div className="input-group">
            <label>Payment Mode</label>
            <select
              value={meta.paymentMode}
              onChange={(e) => setMeta({ ...meta, paymentMode: e.target.value })}
            >
              {PAYMENT_MODES.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>

          <div className="button-group" style={{ marginTop: 16 }}>
            <button className="btn btn-outline" onClick={handleReset}>
              <FaRedo style={{ marginRight: 8 }} /> Reset
            </button>
            <button className="btn btn-primary" onClick={handleDownloadPDF}>
              <FaDownload style={{ marginRight: 8 }} /> Download PDF
            </button>
            <button className="btn btn-primary" onClick={handlePrint}>
              <FaPrint style={{ marginRight: 8 }} /> Print
            </button>
          </div>
        </div>

        <div className="calculator-results">
          <h3 style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
            <FaGasPump /> Bill Preview
          </h3>

          <BillPreview
            template={template}
            brand={brand}
            activeLogoUrl={activeLogoUrl}
            customer={customer}
            copyLabel={copyLabel}
            meta={meta}
            fuelType={fuelType}
            computed={computed}
          />
        </div>
      </div>
    </div>
  );
};

const PreviewRow = ({ label, value }) => {
  if (value === undefined || value === null || value === '') return null;
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: 6,
        fontSize: 14,
      }}
    >
      <span style={{ fontWeight: 700 }}>{label}:</span>
      <span>{value}</span>
    </div>
  );
};

const BillPreview = ({
  template,
  brand,
  activeLogoUrl,
  customer,
  copyLabel,
  meta,
  fuelType,
  computed,
}) => {
  const cardBase = {
    background: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: 12,
    margin: '0 auto',
    boxShadow: '0 2px 12px rgba(15, 23, 42, 0.08)',
    fontFamily: 'Helvetica, Arial, sans-serif',
    color: '#0f172a',
    overflow: 'hidden',
  };

  // ---- 1. CLASSIC THERMAL ----
  if (template === 'classic') {
    return (
      <div style={{ ...cardBase, padding: '24px 22px', maxWidth: 360 }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
          <img src={activeLogoUrl} alt={brand.name} style={{ width: 96, height: 96, objectFit: 'contain' }} />
        </div>
        {customer.name && (
          <div style={{ textAlign: 'center', fontWeight: 700, fontSize: 18, marginTop: 4 }}>{customer.name}</div>
        )}
        {customer.address && (
          <div style={{ textAlign: 'center', fontSize: 14, marginTop: 4 }}>{customer.address}</div>
        )}
        {copyLabel && (
          <div style={{ textAlign: 'center', fontWeight: 700, fontSize: 14, marginTop: 8, letterSpacing: 1 }}>
            {copyLabel.toUpperCase()}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 14, fontSize: 15 }}>
          <span>{meta.date || '-'}</span>
          <span>{meta.time || '-'}</span>
        </div>
        <PreviewRow label="GSTIN" value={meta.gstin} />
        <PreviewRow label="INVOICE NO" value={meta.invoiceNo} />
        <PreviewRow label="VEHICLE NO" value={meta.vehicleNo} />
        <PreviewRow label="NOZZLE NO" value={meta.nozzleNo} />
        <PreviewRow label="PRODUCT" value={fuelType} />
        <PreviewRow label="DENSITY" value={meta.density ? `${meta.density} Kg/m³` : '-  Kg/m³'} />
        <PreviewRow label="RATE" value={`${computed.rate.toFixed(2)}  INR/Ltr`} />
        <PreviewRow label="VOLUME" value={`${computed.volume.toFixed(2)}  Ltr`} />
        <PreviewRow label="AMOUNT" value={`${Math.round(computed.amount)}  INR`} />
        <PreviewRow label="MODE" value={meta.paymentMode} />
        <div style={{ textAlign: 'center', fontWeight: 700, marginTop: 18, color: brand.primary }}>
          Thank you Visit Again
        </div>
      </div>
    );
  }

  // ---- 2. BOLD BRAND BANNER ----
  if (template === 'banner') {
    return (
      <div style={{ ...cardBase, maxWidth: 380 }}>
        <div style={{ background: brand.primary, color: '#fff', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
          <img src={activeLogoUrl} alt={brand.name} style={{ width: 48, height: 48, objectFit: 'contain', background: '#fff', borderRadius: 6, padding: 4 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: 16, letterSpacing: 1 }}>{brand.name.toUpperCase()}</div>
            <div style={{ fontSize: 11, opacity: 0.9 }}>TAX INVOICE</div>
            {meta.gstin && <div style={{ fontSize: 10, opacity: 0.85 }}>GSTIN {meta.gstin}</div>}
          </div>
          {copyLabel && (
            <div style={{ fontSize: 10, fontWeight: 700, background: 'rgba(255,255,255,0.2)', padding: '3px 6px', borderRadius: 4 }}>
              {copyLabel.toUpperCase()}
            </div>
          )}
        </div>
        <div style={{ padding: 16 }}>
          {customer.name && <div style={{ fontWeight: 700, fontSize: 14 }}>{customer.name}</div>}
          {customer.address && <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{customer.address}</div>}
          <div style={{ borderTop: `1px solid ${brand.primary}`, margin: '10px 0', opacity: 0.4 }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
            <span>Date: {meta.date}</span>
            <span>Time: {meta.time}</span>
          </div>
          <div style={{ fontSize: 12, marginTop: 8, lineHeight: 1.7 }}>
            {meta.invoiceNo && <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Invoice</span><span>{meta.invoiceNo}</span></div>}
            {meta.vehicleNo && <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Vehicle</span><span>{meta.vehicleNo}</span></div>}
            {meta.nozzleNo && <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Nozzle</span><span>{meta.nozzleNo}</span></div>}
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Product</span><span>{fuelType}</span></div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Rate</span><span>₹{computed.rate.toFixed(2)} /L</span></div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Volume</span><span>{computed.volume.toFixed(2)} L</span></div>
          </div>
          <div style={{ background: brand.primary, color: '#fff', padding: '10px 14px', borderRadius: 6, marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontWeight: 700, fontSize: 16 }}>TOTAL</span>
            <span style={{ fontWeight: 800, fontSize: 18 }}>₹ {Math.round(computed.amount)}</span>
          </div>
          <div style={{ textAlign: 'center', fontSize: 11, marginTop: 8, color: '#64748b' }}>Paid via {meta.paymentMode}</div>
          <div style={{ textAlign: 'center', fontWeight: 700, marginTop: 8, color: brand.primary, fontSize: 13 }}>
            Thank you Visit Again
          </div>
        </div>
      </div>
    );
  }

  // ---- 3. MINIMAL MONO ----
  if (template === 'minimal') {
    return (
      <div style={{ ...cardBase, padding: '24px 22px', maxWidth: 360, border: '1px solid #e5e7eb', boxShadow: 'none' }}>
        <div style={{ textAlign: 'center', fontWeight: 800, fontSize: 15, letterSpacing: 2 }}>FUEL RECEIPT</div>
        <div style={{ borderTop: '1.5px solid #111827', margin: '10px 0' }} />
        {customer.name && <div style={{ textAlign: 'center', fontWeight: 700, fontSize: 14 }}>{customer.name}</div>}
        {customer.address && <div style={{ textAlign: 'center', fontSize: 11, color: '#64748b', marginTop: 2 }}>{customer.address}</div>}
        <div style={{ textAlign: 'center', marginTop: 16, fontSize: 30, fontWeight: 800 }}>
          ₹ {Math.round(computed.amount)}
        </div>
        <div style={{ textAlign: 'center', fontSize: 11, color: '#64748b', marginTop: 2 }}>
          {computed.volume.toFixed(2)} L @ ₹{computed.rate.toFixed(2)}/L
        </div>
        <div style={{ borderTop: '1px solid #e5e7eb', margin: '14px 0' }} />
        <div style={{ fontSize: 12, lineHeight: 1.8 }}>
          {[
            ['Date', `${meta.date} ${meta.time}`],
            ['Invoice', meta.invoiceNo],
            ['Vehicle', meta.vehicleNo],
            ['Nozzle', meta.nozzleNo],
            ['Product', fuelType],
            ['Payment', meta.paymentMode],
            copyLabel ? ['Copy', copyLabel] : null,
          ].filter(Boolean).map(([k, v]) =>
            v ? (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#64748b' }}>{k}</span>
                <span style={{ fontWeight: 700 }}>{v}</span>
              </div>
            ) : null
          )}
        </div>
        <div style={{ textAlign: 'center', fontSize: 11, fontStyle: 'italic', color: '#64748b', marginTop: 14 }}>
          Thank you. Drive safe.
        </div>
      </div>
    );
  }

  // ---- 4. SIDE STRIP ----
  if (template === 'sidebar') {
    return (
      <div style={{ ...cardBase, display: 'flex', maxWidth: 380 }}>
        <div style={{ background: brand.primary, width: 14, flexShrink: 0 }} />
        <div style={{ padding: '18px 16px', flex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}>
            <img src={activeLogoUrl} alt={brand.name} style={{ width: 72, height: 72, objectFit: 'contain' }} />
          </div>
          {customer.name && <div style={{ textAlign: 'center', fontWeight: 700, fontSize: 15 }}>{customer.name}</div>}
          {customer.address && <div style={{ textAlign: 'center', fontSize: 11, color: '#64748b', marginTop: 2 }}>{customer.address}</div>}
          {copyLabel && (
            <div style={{ textAlign: 'center', fontWeight: 700, fontSize: 11, color: brand.primary, marginTop: 6, letterSpacing: 1 }}>
              {copyLabel.toUpperCase()}
            </div>
          )}
          <div style={{ borderTop: `1px solid ${brand.primary}`, margin: '10px 0', opacity: 0.5 }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
            <span>{meta.date || '-'}</span>
            <span>{meta.time || '-'}</span>
          </div>
          <PreviewRow label="GSTIN" value={meta.gstin} />
          <PreviewRow label="INVOICE" value={meta.invoiceNo} />
          <PreviewRow label="VEHICLE" value={meta.vehicleNo} />
          <PreviewRow label="NOZZLE" value={meta.nozzleNo} />
          <PreviewRow label="PRODUCT" value={fuelType} />
          <PreviewRow label="DENSITY" value={meta.density ? `${meta.density} Kg/m³` : '-'} />
          <PreviewRow label="RATE" value={`${computed.rate.toFixed(2)} /L`} />
          <PreviewRow label="VOLUME" value={`${computed.volume.toFixed(2)} L`} />
          <PreviewRow label="AMOUNT" value={`₹ ${Math.round(computed.amount)}`} />
          <PreviewRow label="MODE" value={meta.paymentMode} />
          <div style={{ textAlign: 'center', fontWeight: 700, marginTop: 14, color: brand.primary }}>
            Thank you Visit Again
          </div>
        </div>
      </div>
    );
  }

  // ---- 5. A4 GST INVOICE ----
  if (template === 'invoice') {
    return (
      <div style={{ ...cardBase, maxWidth: 540 }}>
        <div style={{ background: brand.primary, color: '#fff', padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14 }}>
          <img src={activeLogoUrl} alt={brand.name} style={{ width: 56, height: 56, objectFit: 'contain', background: '#fff', borderRadius: 6, padding: 4 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: 20, letterSpacing: 1 }}>{brand.name.toUpperCase()}</div>
            <div style={{ fontSize: 12, opacity: 0.9 }}>FUEL TAX INVOICE</div>
          </div>
          <div style={{ textAlign: 'right', fontSize: 11 }}>
            {copyLabel && <div style={{ fontWeight: 700, marginBottom: 2 }}>{copyLabel.toUpperCase()}</div>}
            {meta.gstin && <div>GSTIN: {meta.gstin}</div>}
          </div>
        </div>
        <div style={{ padding: 18 }}>
          <div style={{ display: 'flex', gap: 18, fontSize: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>Billed To</div>
              <div>{customer.name || '—'}</div>
              <div style={{ color: '#64748b', marginTop: 2 }}>{customer.address || '—'}</div>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>Invoice Details</div>
              <div>Invoice No: {meta.invoiceNo || '—'}</div>
              <div>Date: {meta.date}  Time: {meta.time}</div>
              <div>Vehicle: {meta.vehicleNo || '—'}</div>
            </div>
          </div>
          <table style={{ width: '100%', marginTop: 14, borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: brand.primary, color: '#fff' }}>
                <th style={{ padding: 6, textAlign: 'left' }}>Product</th>
                <th style={{ padding: 6, textAlign: 'center' }}>Nozzle</th>
                <th style={{ padding: 6, textAlign: 'right' }}>Rate</th>
                <th style={{ padding: 6, textAlign: 'right' }}>Vol (L)</th>
                <th style={{ padding: 6, textAlign: 'right' }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                <td style={{ padding: 6 }}>{fuelType}</td>
                <td style={{ padding: 6, textAlign: 'center' }}>{meta.nozzleNo || '-'}</td>
                <td style={{ padding: 6, textAlign: 'right' }}>{computed.rate.toFixed(2)}</td>
                <td style={{ padding: 6, textAlign: 'right' }}>{computed.volume.toFixed(2)}</td>
                <td style={{ padding: 6, textAlign: 'right' }}>{Math.round(computed.amount)}</td>
              </tr>
            </tbody>
          </table>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10, fontSize: 12 }}>
            <span>Payment Mode: <strong>{meta.paymentMode}</strong></span>
            <span style={{ fontWeight: 800, fontSize: 16, color: brand.primary }}>
              TOTAL ₹ {Math.round(computed.amount)}
            </span>
          </div>
          <div style={{ marginTop: 30, display: 'flex', justifyContent: 'flex-end' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ borderTop: '1px solid #94a3b8', width: 180, paddingTop: 6, fontWeight: 700, fontSize: 11 }}>
                Authorised Signatory
              </div>
            </div>
          </div>
          <div style={{ textAlign: 'center', fontStyle: 'italic', color: brand.primary, marginTop: 10, fontSize: 11 }}>
            Thank you. Visit Again.
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default PetrolBillGenerator;
